import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { crefisaService } from "@/app/services/crefisaService";
import { useCrefisaStream } from "@/app/hooks/useCrefisaStream";
import { useEffect, useRef, useState } from "react";
import {
  CreateSimulationsResponse,
  Simulacao,
} from "@/app/services/crefisaService/createSimulations";
import { useToast } from "@/view/components/ui/use-toast";
import { PartnerType } from "@/app/utils/partners.enum";
import { consentToBankAccount } from "@/app/services/crefisaService/consentToBankAccount";
import { DadoConsentimento } from "@/app/services/crefisaService/getConsent";
import { getAgeFromBirthDate } from "@/app/utils/getAgeFromBirthDate";

const getOffersInssModalSchema = z.object({});

type getOffersInssModalFormData = z.infer<typeof getOffersInssModalSchema>;

interface UseOffersInssModalProps {
  onContinue?: () => void;
}

type SimulationParams = {
  installmentValue?: number;
  codigoInstituicao?: string;
};

export default function useOffersInssModal({
  onContinue,
}: UseOffersInssModalProps) {
  const {
    simulationData,
    customerData,
    setCustomer,
    setProposal,
    setConsentBankAccount,
  } = useCrefisaContext();
  const { toast } = useToast();

  const [selectedInstallment, setSelectedInstallment] =
    useState<Simulacao | null>(null);
  const [installmentInput, setInstallmentInput] = useState("");
  const installmentTouchedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [manualRegistration, setManualRegistration] = useState("");
  const [manualRegistrationDigit, setManualRegistrationDigit] = useState("");
  const [selectedBankCodigo, setSelectedBankCodigo] = useState<string | null>(
    null,
  );
  const [verifiedConsent, setVerifiedConsent] =
    useState<DadoConsentimento | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);

  const getOffersInssModalForm = useForm<getOffersInssModalFormData>({
    resolver: zodResolver(getOffersInssModalSchema),
    mode: "onChange",
  });

  const { handleSubmit } = getOffersInssModalForm;

  const [simulationParams, setSimulationParams] = useState<SimulationParams>(
    {},
  );

  const {
    data,
    steps,
    isStreaming: isFetching,
    isError,
    error,
    retry,
    cancel,
  } = useCrefisaStream<CreateSimulationsResponse>({
    queryKey: [
      "crefisa-inss-simulation",
      simulationData?.clientId,
      simulationParams.installmentValue,
      simulationParams.codigoInstituicao,
    ],
    enabled: !!simulationData?.clientId && !!customerData?.cpf,
    request: ({ onStep, signal }) =>
      crefisaService.createSimulations(
        simulationData!.clientId!,
        {
          cpf: customerData!.cpf,
          isRefinancing: simulationData!.isRefinancing ?? false,
          phone: customerData!.phone,
          type: PartnerType.CREFISA_INSS,
          valorBeneficio: simulationData?.valorBeneficio ?? "",
          possuiEmprestimo: simulationData?.possuiEmprestimo ?? "",
          valorEmprestimoAtivo: simulationData?.valorEmprestimoAtivo ?? "",
          recebeViaCrefisa: simulationData?.recebeViaCrefisa ?? "",
          possuiDebitos: simulationData?.possuiDebitos ?? "",
          valorTotalDebitos: simulationData?.valorTotalDebitos ?? "",
          ...(customerData?.income?.codeReceiptDataId
            ? { codeReceiptDataId: customerData.income.codeReceiptDataId }
            : {}),
          ...(simulationParams.installmentValue
            ? { installmentValue: simulationParams.installmentValue }
            : {}),
          ...(simulationParams.codigoInstituicao
            ? { codigoInstituicao: simulationParams.codigoInstituicao }
            : {}),
        },
        { onStep, signal },
      ),
  });

  useEffect(() => {
    if (!isError) return;

    toast({
      title: "Erro ao gerar proposta",
      description:
        error?.mensagem ||
        error?.message ||
        "Não foi possível realizar a simulação.",
      variant: "destructive",
    });
  }, [isError, error, toast]);

  // Clientes com 100 anos ou mais só podem contratar até 6 parcelas.
  const customerAge = getAgeFromBirthDate(customerData?.birthDate);
  const maxInstallments =
    customerAge !== null && customerAge >= 100 ? 6 : Infinity;

  const installments = (data?.simulacoes ?? []).filter(
    (item) => item.quantidadeParcelas <= maxInstallments,
  );

  const installmentValue = Number(installmentInput) / 100;

  // Valor solicitado (somente leitura): usa o valorSolicitado da parcela
  // selecionada e, quando o limite tomado vem 0, mantém esse valor.
  const displayedValue =
    selectedInstallment?.valorSolicitado || data?.valorLimiteTomado || 0;

  useEffect(() => {
    if (installments.length) {
      setSelectedInstallment(installments[installments.length - 1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Enquanto o usuário não editar manualmente, o input do valor da parcela
  // reflete a parcela selecionada.
  useEffect(() => {
    if (installmentTouchedRef.current) return;
    if (!selectedInstallment?.valorParcela) return;

    setInstallmentInput(
      String(Math.round(selectedInstallment.valorParcela * 100)),
    );
  }, [selectedInstallment?.valorParcela]);

  // Limpa o timer pendente ao desmontar.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const scheduleSimulation = (value: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!value) return;
      setSimulationParams((previous) => ({
        ...previous,
        installmentValue: value,
      }));
    }, 1500);
  };

  const handleInstallmentValueChange = (value: string) => {
    installmentTouchedRef.current = true;
    const digits = value.replace(/\D/g, "").slice(0, 12);
    setInstallmentInput(digits);
    scheduleSimulation(Number(digits) / 100);
  };

  const handleSelectInstallment = (idSimulacao: string) => {
    const installment = installments.find((i) => i.idSimulacao === idSimulacao);
    if (!installment) return;
    // Ao trocar a parcela, o input volta a espelhar o valor da nova seleção.
    installmentTouchedRef.current = false;
    setSelectedInstallment(installment);
  };

  const handleSelectBank = (codigo: string) => {
    setSelectedBankCodigo(codigo);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setSimulationParams((previous) => ({
      ...previous,
      codigoInstituicao: codigo,
      ...(installmentValue ? { installmentValue } : {}),
    }));
  };

  const consentLink =
    data?.linkConsentimento ?? data?.consentimento?.linkConsentimento;
  const banksInfos = data?.banksInfos ?? [];

  const requiresConsent =
    data?.requerConsentimento ?? !!simulationData?.naoRecebeViaCrefisa;

  const simConsent = data?.dadosConsentimento?.[0];
  const bankDataConsentId = simConsent?.dadosConsentimentoId;
  const authorizedConsent = verifiedConsent
    ? {
        nomeBanco: verifiedConsent.nomeBanco,
        agencia: verifiedConsent.agencia,
        conta: verifiedConsent.conta,
        digitoConta: verifiedConsent.digitoConta,
      }
    : simConsent && simConsent.conta
      ? {
          nomeBanco: simConsent.nomeBanco,
          agencia: simConsent.agencia,
          conta: simConsent.conta,
          digitoConta: simConsent.digitoConta,
        }
      : null;
  const isConsentAuthorized = !!authorizedConsent;

  useEffect(() => {
    if (simConsent && simConsent.conta) {
      const bankAccount = consentToBankAccount(simConsent);

      setConsentBankAccount(bankAccount);
      setCustomer({
        ...(bankDataConsentId ? { dataConsentId: bankDataConsentId } : {}),
        bankAccount,
      });
    }
  }, [simConsent, bankDataConsentId, setCustomer, setConsentBankAccount]);

  const handleVerifyConsent = async () => {
    if (!simulationData?.clientId) return;

    try {
      setConsentLoading(true);

      const consent = await crefisaService.getConsent(simulationData.clientId);
      const item = consent.dadosConsentimento?.[0];

      if (!consent.consentimentoRealizado || !item) {
        toast({
          title: "Consentimento pendente",
          description: consent.mensagem || "Consentimento ainda não realizado",
        });
        return;
      }

      const bankAccount = consentToBankAccount(item);

      setVerifiedConsent(item);
      setConsentBankAccount(bankAccount);
      setCustomer({
        ...(item.dadosConsentimentoId
          ? { dataConsentId: item.dadosConsentimentoId }
          : {}),
        bankAccount,
      });

      toast({
        title: "Consentimento autorizado",
        description:
          consent.mensagem || "Consentimento autorizado pelo cliente",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao verificar consentimento",
        description: error?.message || "Erro ao verificar consentimento",
        variant: "destructive",
      });
    } finally {
      setConsentLoading(false);
    }
  };

  const handleCopyConsentLink = async () => {
    if (!consentLink) return;

    try {
      await navigator.clipboard.writeText(consentLink);
      toast({
        title: "Link copiado",
        description:
          "Link do consentimento copiado para a área de transferência.",
      });
    } catch {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o link.",
        variant: "destructive",
      });
    }
  };

  const requiresManualRegistration = !data?.matricula || !data?.digitoMatricula;
  const isManualRegistrationValid =
    !requiresManualRegistration ||
    (!!manualRegistration && !!manualRegistrationDigit);

  const submitModal = handleSubmit(() => {
    if (!selectedInstallment) return;

    if (!isManualRegistrationValid) {
      toast({
        title: "Preencha o NIS",
        description: "Informe o NIS e o dígito para prosseguir.",
        variant: "destructive",
      });
      return;
    }

    if (requiresConsent && !isConsentAuthorized) {
      toast({
        title: "Consentimento obrigatório",
        description:
          "É obrigatório realizar o consentimento para finalizar a proposta deste cliente.",
        variant: "destructive",
      });
      return;
    }

    setCustomer({
      income: {
        ...customerData?.income,
        registration: data?.matricula ?? manualRegistration,
        registrationDigit: data?.digitoMatricula ?? manualRegistrationDigit,
        valueIncome: data?.renda || Number(simulationData?.valorBeneficio) || 0,
        codeBenefitSpecie: 0,
      },
    });

    setProposal({
      value: selectedInstallment.valorSolicitado,
      prazo: selectedInstallment.quantidadeParcelas,
      simulationId: selectedInstallment.idSimulacao,
      refinancingId: selectedInstallment.idContratoRefin,
    });
    onContinue?.();
  });

  const errorMessage =
    !isFetching && data?.sucesso === false
      ? data?.mensagemLimiteTomado || "Não foi possível realizar a simulação."
      : null;

  return {
    form: getOffersInssModalForm,
    submitModal,
    isPending: isFetching,
    isFetching,
    steps,
    retry,
    cancel,
    isError,
    installments,
    selectedInstallment,
    handleSelectInstallment,
    displayedValue,
    installmentInput,
    installmentValue,
    handleInstallmentValueChange,
    commission: data?.commission,
    matricula: data?.matricula,
    digitoMatricula: data?.digitoMatricula,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    banksInfos,
    selectedBankCodigo,
    handleSelectBank,
    consentLink,
    consentMessage: data?.mensagem,
    handleCopyConsentLink,
    errorMessage,
    isManualRegistrationValid,
    requiresConsent,
    authorizedConsent,
    isConsentAuthorized,
    consentLoading,
    handleVerifyConsent,
  };
}
